import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/shared/SEOHead';
import { useTranslation } from 'react-i18next';

export function TeamReportPage() {
  const { links } = useSiteSettings();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'aligned' | 'split'>('aligned');

  const data = {
    aligned: {
      label: 'An aligned team',
      pos: [42, 47, 50, 52, 55, 58, 61, 64, 67],
      counts: ['Very high = 0', 'High = 2', 'Mid-range = 6', 'Low = 1', 'Very low = 0'],
      html: "<p>Everyone sits near the middle. The team score is a fair description of almost every person in the room, so a conversation about this factor will feel <strong>recognisable to the whole group</strong> &mdash; and any change has to be made by all of them together.</p>"
    },
    split: {
      label: 'A split team',
      pos: [10, 14, 18, 22, 55, 90, 92, 94, 96],
      counts: ['Very high = 4', 'High = 0', 'Mid-range = 1', 'Low = 2', 'Very low = 2'],
      html: "<p>Identical team score, but nobody is actually at it. Four people are at one extreme and four at the other. This team doesn't have a moderate preference &mdash; it has <strong>two factions who have probably been irritating each other for months</strong> without knowing why.</p>"
    }
  };

  const currentData = data[viewMode];

  return (
    <main className="pt-20">
      <SEOHead 
        title="What's inside the FIVE LENS Team Report | FIVE LENS People Development"
        description="A plain-English preview of the FIVE LENS Team Report: what the five lenses look like when they're read across a whole team, how to read a group score, and what a page actually contains."
        canonicalUrl="/reports/team"
      />
      <div className="pfrt">

        {/* ============================ HERO ============================ */}
        <header className="pfrt__hero">
          <div className="pfrt__wrap">
            <nav className="pfrt-tier" aria-label="Team report versions">
              <span className="pfrt-tier__label">Report</span>
              <span className="pfrt-tier__item pfrt-tier__item--on" aria-current="page">Team Report</span>
              <Link className="pfrt-tier__item" to="/reports/team-leader">Team Leader Report &rarr;</Link>
            </nav>
            <p className="pfrt__eyebrow">FIVE LENS People Development &middot; Team Report &middot; Preview</p>
            <h1>One mark on the scale. Nine people behind it.</h1>
            <p className="pfrt__lede">The Team Report reads a whole team through the same five lenses used for an individual, then adds three chapters that only make sense for a group. Every reading is an average &mdash; so the report always shows you the spread underneath it. Every member of the team gets the same copy.</p>

            <figure className="pfrt-scale" aria-hidden="true">
              <div className="pfrt-scale__anchors"><span>One end of the scale</span><span>The other end</span></div>
              <div className="pfrt-scale__rail">
                <div className="pfrt-scale__tmark" style={{"--pos":"58%"} as React.CSSProperties}><span>T</span></div>
                <div className="pfrt-scale__band"></div>
                <div className="pfrt-scale__rule"></div>
              </div>
            </figure>
            <p style={{marginTop:"18px", fontSize:"15px", color:"var(--ink-2)", maxWidth:"48ch"}}>The red <strong>T</strong> is the team's position. It's the first thing people look at, and on its own it's the least useful number in the report.</p>

            <ul className="pfrt__hero-meta">
              <li>Team Report</li>
              <li>Everyone in the team gets a copy</li>
              <li>11 pages</li>
              <li>5 lenses + 3 team chapters</li>
            </ul>
          </div>
        </header>

        {/* ============================ HOW TO READ A TEAM SCORE ============================ */}
        <section className="pfrt__section">
          <div className="pfrt__wrap">
            <div className="pfrt__head">
              <div className="pfrt__head-row">
                <p className="pfrt__eyebrow">01 &mdash; The one skill you need</p>
                <div className="pfrt__ticks"></div>
              </div>
              <h2>The average hides the argument</h2>
              <p className="pfrt__lede">These two teams have the same score on the same scale. The T sits in exactly the same place. They are not the same team. Switch between them.</p>
            </div>

            <div className="pfrt-demo">
              <div className="pfrt-panel">
                <div className="pfrt-choices" role="group" aria-label="Team shape">
                  <button 
                    className="pfrt-choice" 
                    type="button" 
                    aria-pressed={viewMode === 'aligned'}
                    onClick={() => setViewMode('aligned')}
                  >
                    An aligned team
                  </button>
                  <button 
                    className="pfrt-choice" 
                    type="button" 
                    aria-pressed={viewMode === 'split'}
                    onClick={() => setViewMode('split')}
                  >
                    A split team
                  </button>
                </div>

                <figure className="pfrt-scale">
                  <div className="pfrt-scale__anchors"><span>Low</span><span>High</span></div>
                  <div className="pfrt-scale__rail">
                    <div className="pfrt-scale__tmark" style={{"--pos":"55%"} as React.CSSProperties}><span>T</span></div>
                    <div className="pfrt-scale__band" style={{"--hue":"var(--rule-5)"} as React.CSSProperties}></div>
                    <div className="pfrt-scale__rule"></div>
                    <div className="pfrt-dots">
                      {currentData.pos.map((pos, i) => (
                        <span key={i} className="pfrt-dot" style={{ left: `${pos}%` }}></span>
                      ))}
                    </div>
                  </div>
                  <figcaption className="pfrt-legend">
                    <span>&#9679; one team member</span>
                    <span style={{color:"var(--tmark)"}}>&#9660; team score</span>
                  </figcaption>
                </figure>

                <ul className="pfrt-counts">
                  {currentData.counts.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>

              <div>
                <span className="pfrt-readout__label">{currentData.label}</span>
                <div className="pfrt-readout__body" dangerouslySetInnerHTML={{ __html: currentData.html }}></div>

                <ul className="pfrt-rules">
                  <li><b>i</b><span>The report prints the <strong>spread as well as the score</strong> &mdash; how many members sit very high, high, mid-range, low and very low on each factor.</span></li>
                  <li><b>ii</b><span>A middling team score is often <strong>two camps, not a consensus</strong>. That's usually where the friction in a team already lives.</span></li>
                  <li><b>iii</b><span>No individual is named. The report describes the group, and members recognise themselves in it if they choose to.</span></li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============================ WHAT IT COVERS ============================ */}
        <section className="pfrt__section">
          <div className="pfrt__wrap">
            <div className="pfrt__head">
              <div className="pfrt__head-row">
                <p className="pfrt__eyebrow">02 &mdash; What's in it</p>
                <div className="pfrt__ticks"></div>
              </div>
              <h2>Eight readings on the team as a whole</h2>
              <p className="pfrt__lede">The first five are the individual lenses, read collectively. The last three exist only in the team report.</p>
            </div>

            <ol className="pfrt-chapters">
              <li className="pfrt-chapter pfrt-chapter--1">
                <div className="pfrt-chapter__num">01</div>
                <div><h3>Team Mastery</h3><p className="pfrt-chapter__q"><em>What is this group good at, and what does it avoid?</em></p></div>
                <div className="pfrt-chapter__what">Six factors read across everyone, then reduced to three things worth acting on: the team's strongest factor, its weakest, and the runner-up. Each comes with the spread behind it, so you can see whether the finding is the whole group or a vocal few.</div>
              </li>
              <li className="pfrt-chapter pfrt-chapter--2">
                <div className="pfrt-chapter__num">02</div>
                <div><h3>Team Emotional Resilience</h3><p className="pfrt-chapter__q"><em>How much pressure can this group absorb?</em></p></div>
                <div className="pfrt-chapter__what">One reading on how well the team recovers from setbacks, and what follows from it &mdash; how much stress to put on people, how much supportive leadership will be needed, and whether the resilient members can carry the others.</div>
              </li>
              <li className="pfrt-chapter pfrt-chapter--3">
                <div className="pfrt-chapter__num">03</div>
                <div><h3>The team's primary social drive</h3><p className="pfrt-chapter__q"><em>What is this group quietly protecting?</em></p></div>
                <div className="pfrt-chapter__what">Whether the team is mostly focused on being safe, on belonging, on being recognised, or on contributing beyond itself. The dominant drive explains a lot of group behaviour that otherwise looks like personality clashes.</div>
              </li>
              <li className="pfrt-chapter pfrt-chapter--4">
                <div className="pfrt-chapter__num">04</div>
                <div><h3>The team's primary energy centre</h3><p className="pfrt-chapter__q"><em>Does this group think, feel, or act first?</em></p></div>
                <div className="pfrt-chapter__what">A group that leans hard on one centre has a predictable strength and a predictable failure. A thinking-heavy team analyses well and stalls under time pressure; the report says so plainly, in both directions.</div>
              </li>
              <li className="pfrt-chapter pfrt-chapter--5">
                <div className="pfrt-chapter__num">05</div>
                <div><h3>Team Enneagram styles</h3><p className="pfrt-chapter__q"><em>Which patterns are over-represented here?</em></p></div>
                <div className="pfrt-chapter__what">The style the team has most of, the one backing it up, and the one it has least of. The missing style is usually the most useful finding &mdash; it's the perspective nobody in the room is bringing.</div>
              </li>
              <li className="pfrt-chapter pfrt-chapter--6">
                <div className="pfrt-chapter__num">06</div>
                <div><h3>How the team moves around people</h3><p className="pfrt-chapter__q"><em>Push, comply, or withdraw?</em></p></div>
                <div className="pfrt-chapter__what">Under pressure, people move in one of three directions. This chapter shows how the team splits across the three, with counts. A team with six assertive members and two who withdraw has a meeting problem it can now name.</div>
              </li>
              <li className="pfrt-chapter pfrt-chapter--7">
                <div className="pfrt-chapter__num">07</div>
                <div><h3>Social direction</h3><p className="pfrt-chapter__q"><em>How outward-facing is this group?</em></p></div>
                <div className="pfrt-chapter__what">Where the team sits between extroverted and introverted, and the practical consequences for how you run meetings &mdash; agendas in advance, time to think, and making sure the quiet half is actually heard.</div>
              </li>
              <li className="pfrt-chapter pfrt-chapter--8">
                <div className="pfrt-chapter__num">08</div>
                <div><h3>Team and leader side by side</h3><p className="pfrt-chapter__q"><em>Where does the leader differ from the team?</em></p></div>
                <div className="pfrt-chapter__what">A chart of all nine styles with the team's profile in colour and the leader's laid over it in grey. The gaps between the two are the conversation. The leader receives a separate report that works through them in detail.</div>
              </li>
            </ol>
          </div>
        </section>

        {/* ============================ SAMPLE PAGE ============================ */}
        <section className="pfrt__section">
          <div className="pfrt__wrap">
            <div className="pfrt__head">
              <div className="pfrt__head-row">
                <p className="pfrt__eyebrow">03 &mdash; What a page looks like</p>
                <div className="pfrt__ticks"></div>
              </div>
              <h2>Every page is built the same way</h2>
              <p className="pfrt__lede">Reading on the left, consequences in the middle, context and questions in the teal panel. The written feedback is held back here &mdash; that part belongs in your team's own report.</p>
            </div>

            <article className="pfrt-page">
              <div className="pfrt-page__bar">
                <div className="pfrt-page__brand">FIVE LENS<span className="pfrt-page__tier">Team Report</span></div>
                <div className="pfrt-page__title">
                  <strong>Lens 4: Team's Primary Energy Centre</strong>
                  <span>Does this group think, feel, or act first?</span>
                </div>
              </div>
              <div className="pfrt-page__cols">
                <div className="pfrt-page__col">
                  <p className="pfrt-page__label">The team's reading</p>
                  <div className="pfrt-scale__rail">
                    <div className="pfrt-scale__tmark" style={{"--pos":"71%"} as React.CSSProperties}><span>T</span></div>
                    <div className="pfrt-scale__band" style={{"--hue":"var(--rule-5)", height:"24px"} as React.CSSProperties}></div>
                    <div className="pfrt-scale__rule"></div>
                  </div>
                  <div className="pfrt-page__anchors">
                    <div><b>Weak</b><ul><li>Acts on impulse</li><li>Rigid beliefs</li><li>False confidence</li></ul></div>
                    <div><b>Strong</b><ul><li>Acts on analysis</li><li>Broad perspective</li><li>Possible self-doubt</li></ul></div>
                  </div>
                  <p className="pfrt-page__spread">Very high = 4 &nbsp; High = 2 &nbsp; Mid = 1 &nbsp; Low = 2 &nbsp; Score = 49</p>
                  <p className="pfrt-page__stamp">The score, and the count of members sitting in each band beneath it.</p>
                </div>
                <div className="pfrt-page__col">
                  <p className="pfrt-page__label">What this could mean to the team</p>
                  <ul className="pfrt-redact">
                    <li><span></span><span></span><span style={{width:"56%"}}></span></li>
                    <li><span></span><span style={{width:"72%"}}></span></li>
                    <li><span></span><span></span><span style={{width:"44%"}}></span></li>
                  </ul>
                  <p className="pfrt-page__stamp">Three or four consequences, written for the group rather than for an individual &mdash; how this shows up in decisions, meetings and relationships.</p>
                </div>
                <div className="pfrt-page__col pfrt-page__col--aside">
                  <p className="pfrt-page__label">Insights</p>
                  <ul className="pfrt-redact"><li><span></span><span></span><span style={{width:"60%"}}></span></li></ul>
                  <p className="pfrt-page__label" style={{marginTop:"24px"}}>Reflections</p>
                  <ul className="pfrt-redact"><li><span style={{width:"90%"}}></span></li><li><span style={{width:"74%"}}></span></li></ul>
                  <p className="pfrt-page__stamp">Context, then questions for the workshop.</p>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* ============================ NOTES ============================ */}
        <section className="pfrt__section">
          <div className="pfrt__wrap">
            <div className="pfrt__head">
              <div className="pfrt__head-row">
                <p className="pfrt__eyebrow">04 &mdash; Before you circulate it</p>
                <div className="pfrt__ticks"></div>
              </div>
              <h2>Three things worth saying up front</h2>
            </div>
            <ul className="pfrt-notes">
              <li>
                <b>Nobody is named</b>
                <p>The report describes the group. Individual results stay in individual reports, and no member is identified as the high or low score on any scale.</p>
              </li>
              <li>
                <b>It has a lock date</b>
                <p>The team profile is built from whoever had completed the questionnaire on the lock date. Change the membership and you change the profile.</p>
              </li>
              <li>
                <b>It's workshop material</b>
                <p>Handing it out cold rarely lands. It's written to be worked through together with a certified practitioner, ending in agreed actions.</p>
              </li>
            </ul>
          </div>
        </section>

        {/* ============================ PROCESS ============================ */}
        <section className="pfrt__section">
          <div className="pfrt__wrap">
            <div className="pfrt__head">
              <div className="pfrt__head-row">
                <p className="pfrt__eyebrow">05 &mdash; How it runs</p>
                <div className="pfrt__ticks"></div>
              </div>
              <h2>From questionnaire to agreed actions</h2>
            </div>
            <ol className="pfrt-steps">
              <li><div><b>Everyone completes the questionnaire</b><p>Online, about twenty minutes each. The team profile is only as good as its coverage, so partial completion weakens every reading in the report.</p></div></li>
              <li><div><b>The team is locked</b><p>On the lock date the membership is fixed and the profile is calculated from it. That date is printed on the cover so you always know which version of the team you're reading.</p></div></li>
              <li><div><b>Reports are issued</b><p>Every member receives the Team Report. The leader also receives a separate Team Leader Report comparing their own profile against the group's.</p></div></li>
              <li><div><b>The team works through it together</b><p>A Team Discovery Day or the longer Team Performance Process, run by a certified practitioner. This is where the spread behind each score gets discussed rather than politely ignored.</p></div></li>
              <li><div><b>You leave with actions and owners</b><p>Specific commitments with names attached, not a general resolution to communicate better. Additional skills modules can follow where a particular gap needs work.</p></div></li>
            </ol>
          </div>
        </section>

        {/* ============================ CTA ============================ */}
        <section className="pfrt__section" style={{borderTop:0}}>
          <div className="pfrt__wrap">
            <div className="pfrt-cta">
              <div>
                <h2>Thinking about running this with your team?</h2>
                <p>A certified practitioner can talk through team size, timing and which process fits &mdash; a single discovery day or a programme over several months.</p>
              </div>
              <div className="pfrt-cta__actions">
                <a className="pfrt-btn pfrt-btn--solid" href={links.bookingUrl} target="_blank" rel="noopener noreferrer" style={{ visibility: 'visible', opacity: 1, position: 'relative', zIndex: 10 }}>Book a conversation</a>
                <Link className="pfrt-btn pfrt-btn--ghost" to="/reports/team-leader">See the Team Leader Report</Link>
              </div>
            </div>
          </div>
        </section>

        <footer className="pfrt__foot">
          <div className="pfrt__wrap">
            <p>This page is an illustrative preview. Scale positions, distributions and page content shown here are for demonstration only and are not drawn from any real team. The FIVE LENS Questionnaire, the Team Report and its interpretive content are the property of FIVE LENS People Development (Pty) Ltd, part of Ennea International Group. &copy; FIVE LENS People Development.</p>
          </div>
        </footer>

      </div>
    </main>
  );
}
